import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    Plus, Calendar, Users, AlertTriangle, CheckCircle,
    ChevronRight, FileText, User, Camera
} from 'lucide-react';
import { Button } from '@components/ui';
import DatePickerInput from '@components/ui/DatePickerInput';
import {
    getRaporlarByInsaat,
    getSeflerByInsaat,
} from '@mocks/santiyeGunlugu';
import GunlukModal from './GunlukModal';
import GunlukDetayModal from './GunlukDetayModal';
import clsx from 'clsx';

const toISODate = (d) => d.toISOString().split('T')[0];

const QUICK_FILTERS = [
    { key: 'bugun', label: 'Bugün' },
    { key: 'bu_hafta', label: 'Bu Hafta' },
    { key: 'bu_ay', label: 'Bu Ay' },
    { key: 'aralık', label: 'Tarih Aralığı' },
];

function filterByPeriod(raporlar, activeFilter, baslangic, bitis) {
    const today = toISODate(new Date());

    if (activeFilter === 'bugun') return raporlar.filter(r => r.tarih === today);

    if (activeFilter === 'bu_hafta') {
        const bugun = new Date();
        const bas = new Date(bugun); bas.setDate(bugun.getDate() - bugun.getDay() + 1);
        const son = new Date(bas); son.setDate(bas.getDate() + 6);
        return raporlar.filter(r => r.tarih >= toISODate(bas) && r.tarih <= toISODate(son));
    }

    if (activeFilter === 'bu_ay') {
        const d = new Date();
        const prefix = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return raporlar.filter(r => r.tarih.startsWith(prefix));
    }

    if (activeFilter === 'aralık') {
        let data = raporlar;
        if (baslangic) data = data.filter(r => r.tarih >= toISODate(baslangic));
        if (bitis) data = data.filter(r => r.tarih <= toISODate(bitis));
        return data;
    }

    return raporlar; // 'tumu'
}

export default function GunlukRapor({ insaatId, isAdmin, sefId, raporStats }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [detayModalOpen, setDetayModalOpen] = useState(false);
    const [selectedRapor, setSelectedRapor] = useState(null);
    const [selectedSef, setSelectedSef] = useState('all');
    const [activeFilter, setActiveFilter] = useState('bu_ay');
    const [baslangicDate, setBaslangicDate] = useState(null);
    const [bitisDate, setBitisDate] = useState(null);

    const sefler = getSeflerByInsaat(insaatId);

    const raporlar = useMemo(() => {
        // Şef ise sadece kendi raporları, admin ise seçilen şef
        const filtreSefId = isAdmin
            ? (selectedSef === 'all' ? null : selectedSef)
            : sefId; // şef: kendi ID'si

        let data = getRaporlarByInsaat(insaatId, filtreSefId);
        return filterByPeriod(data, activeFilter, baslangicDate, bitisDate);
    }, [insaatId, isAdmin, sefId, selectedSef, activeFilter, baslangicDate, bitisDate]);

    const formatTarih = (tarihStr) => new Date(tarihStr).toLocaleDateString('tr-TR', {
        weekday: 'long', day: 'numeric', month: 'long'
    });

    const handleSubmit = (data) => {
        toast.success('Rapor kaydedildi');
        setModalOpen(false);
    };

    return (
        <div className="space-y-5">
            {/* Mini Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-bg-secondary rounded-xl p-3 text-center border border-border-light">
                    <p className="text-2xl font-bold text-primary">{raporStats?.toplamRapor ?? raporlar.length}</p>
                    <p className="text-xs text-text-muted mt-0.5">Toplam Rapor</p>
                </div>
                <div className="bg-bg-secondary rounded-xl p-3 text-center border border-border-light">
                    <p className="text-2xl font-bold text-warning">{raporStats?.sorunluGunler ?? 0}</p>
                    <p className="text-xs text-text-muted mt-0.5">Sorunlu Gün</p>
                </div>
                <div className="hidden sm:block bg-bg-secondary rounded-xl p-3 text-center border border-border-light">
                    <p className="text-2xl font-bold text-success">{raporStats?.ortPersonel ?? 0}</p>
                    <p className="text-xs text-text-muted mt-0.5">Ort. Personel</p>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Admin: Şef Filtresi */}
                        {isAdmin && sefler.length > 1 && (
                            <div className="flex items-center gap-2 bg-bg-secondary border border-border-light rounded-xl px-3 py-2">
                                <User size={14} className="text-text-muted shrink-0" />
                                <select
                                    value={selectedSef}
                                    onChange={(e) => setSelectedSef(e.target.value)}
                                    className="bg-transparent border-none text-sm text-text-primary focus:outline-none cursor-pointer"
                                >
                                    <option value="all">Tüm Şefler</option>
                                    {sefler.map(sef => (
                                        <option key={sef.id} value={sef.id}>{sef.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Hızlı Filtreler */}
                        <div className="flex items-center gap-1 bg-bg-tertiary rounded-xl p-1">
                            {QUICK_FILTERS.map(f => (
                                <button key={f.key} onClick={() => setActiveFilter(f.key)}
                                    className={clsx(
                                        "px-3.5 py-1.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap",
                                        activeFilter === f.key
                                            ? "bg-primary text-white shadow-sm"
                                            : "text-text-muted hover:text-text-primary"
                                    )}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Button variant="primary" icon={<Plus size={16} />}
                        onClick={() => { setSelectedRapor(null); setModalOpen(true); }}>
                        Rapor Oluştur
                    </Button>
                </div>

                {/* Tarih Aralığı — sadece 'aralık' aktifken */}
                {activeFilter === 'aralık' && (
                    <div className="flex items-center gap-3 p-3 bg-bg-secondary rounded-xl border border-border-light flex-wrap">
                        <span className="text-sm text-text-muted font-medium">Tarih aralığı:</span>
                        <div className="flex items-center gap-3 flex-1 flex-wrap">
                            <div className="flex-1 min-w-[160px]">
                                <DatePickerInput
                                    value={baslangicDate}
                                    onChange={setBaslangicDate}
                                    placeholder="Başlangıç tarihi"
                                    maxDate={bitisDate}
                                    clearable
                                />
                            </div>
                            <span className="text-text-muted text-sm">—</span>
                            <div className="flex-1 min-w-[160px]">
                                <DatePickerInput
                                    value={bitisDate}
                                    onChange={setBitisDate}
                                    placeholder="Bitiş tarihi"
                                    minDate={baslangicDate}
                                    clearable
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Rapor Listesi */}
            {raporlar.length > 0 ? (
                <div className="space-y-3">
                    {raporlar.map((rapor) => {
                        const sorunluMu = rapor.sorunlar?.length > 0;

                        return (
                            <div key={rapor.id}
                                onClick={() => { setSelectedRapor(rapor); setDetayModalOpen(true); }}
                                className={clsx(
                                    "group cursor-pointer rounded-2xl border bg-bg-secondary transition-all duration-200",
                                    "hover:shadow-md hover:border-primary/20",
                                    sorunluMu ? "border-l-4 border-l-warning border-r border-t border-b border-border-light" : "border-border-light"
                                )}>
                                {/* Kart Header */}
                                <div className="flex items-center justify-between px-5 py-4 border-b border-border-light">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                            <Calendar size={18} className="text-primary" />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="font-semibold text-text-primary capitalize truncate">
                                                {formatTarih(rapor.tarih)}
                                            </h3>
                                            {isAdmin && (
                                                <p className="text-xs text-text-muted">{rapor.kaydeden}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {rapor.photoCount > 0 && (
                                            <div className="flex items-center gap-1 bg-primary/10 px-2 py-1 rounded-lg">
                                                <Camera size={11} className="text-primary" />
                                                <span className="text-xs font-medium text-primary">{rapor.photoCount}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1.5 bg-bg-tertiary px-2.5 py-1 rounded-lg">
                                            <Users size={13} className="text-text-muted" />
                                            <span className="text-sm font-medium text-text-primary">{rapor.personelSayisi}</span>
                                        </div>
                                        <ChevronRight size={18} className="text-text-muted group-hover:text-primary transition-colors" />
                                    </div>
                                </div>

                                {/* Kart Body */}
                                <div className="px-5 py-3 space-y-2.5">
                                    <p className="text-sm text-text-secondary line-clamp-2 leading-relaxed">{rapor.ozet}</p>
                                    <div className="flex items-center gap-4">
                                        {rapor.yapilanIsler?.length > 0 && (
                                            <span className="flex items-center gap-1.5 text-xs text-success font-medium">
                                                <CheckCircle size={13} /> {rapor.yapilanIsler.length} iş tamamlandı
                                            </span>
                                        )}
                                        {sorunluMu && (
                                            <span className="flex items-center gap-1.5 text-xs text-warning font-medium">
                                                <AlertTriangle size={13} /> {rapor.sorunlar.length} sorun var
                                            </span>
                                        )}
                                    </div>
                                    {rapor.yapilanIsler?.length > 0 && (
                                        <div className="pt-1 border-t border-border-light/60">
                                            <ul className="space-y-1">
                                                {rapor.yapilanIsler.slice(0, 2).map((is, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-xs text-text-muted">
                                                        <span className="text-success mt-0.5 shrink-0">✓</span>
                                                        <span className="line-clamp-1">{is}</span>
                                                    </li>
                                                ))}
                                                {rapor.yapilanIsler.length > 2 && (
                                                    <li className="text-xs text-text-tertiary">+{rapor.yapilanIsler.length - 2} iş daha</li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-16 bg-bg-secondary rounded-2xl border border-border-light">
                    <FileText size={48} className="mx-auto text-text-muted mb-4 opacity-40" />
                    <h3 className="text-lg font-medium text-text-primary mb-1">Bu dönem için rapor yok</h3>
                    <p className="text-sm text-text-muted mb-5">
                        {activeFilter === 'bugun' ? 'Bugün henüz rapor oluşturulmamış.' : 'Seçili dönemde rapor bulunamadı.'}
                    </p>
                    <Button variant="primary" icon={<Plus size={16} />}
                        onClick={() => { setSelectedRapor(null); setModalOpen(true); }}>
                        Rapor Oluştur
                    </Button>
                </div>
            )}

            <GunlukModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
                rapor={selectedRapor}
            />
            <GunlukDetayModal
                isOpen={detayModalOpen}
                onClose={() => setDetayModalOpen(false)}
                rapor={selectedRapor}
            />
        </div>
    );
}
